import { Box, useTheme } from "@mui/material";
import { tokens } from "../../../../../theme";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import ShDataGrid2 from "../../../../../components/theme/datagrid2/ShDataGrid2";
import ShLabel from "../../../../../components/theme/label/ShLabel";
import ShCopyButton from '../../../../../components/theme/ShCopy';
import SidepanelLoader from '../../../../../components/theme/sidepanel2/SidepanelLoader';
import ShLabelSkeleton from "../../../../../components/theme/skeleton/ShLabelSkeleton";
import ShTextSkeleton from "../../../../../components/theme/skeleton/ShTextSkeleton";
import { useGetReturnsQuery } from "../../../../../newapi/inbound/returnsSlice";
import { getReturnStateData } from "../../../../../utils/labelColorText";
import { general_states } from '../../../../../utils/staticEnums';
import { ORDER_FILTER_KEYS } from '../../../../../components/global/DataGrid/Filter';
import Text from "../../../../../components/theme/text/Text";
import { RelationReturnCard } from "../../../../../components/global/cards/inbounds/ReturnCards";

const SpReturnDetails = SidepanelLoader(() => import("../../../../../components/global/Sidepanels/SpReturnDetails/SpReturnDetails"));

let gridOptions = {
    export: true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
    filter: [ORDER_FILTER_KEYS.STATE],
};

const WebshopCredentialsReturns = (props) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [data, setData] = useState([]);
    const [isExtended, setIsExtended] = useState(false);
    const [spLoading, setSpLoading] = useState();
    const [config, setConfig] = useState({
        webshopCredentialsId: props.webshopCredentialsId,
        [ORDER_FILTER_KEYS.STATE.backend_key]: [
            general_states.OPEN,
            general_states.PROCESSING,
            general_states.PROCESSED
        ],
        stateAll: false
    });

    const ref = useRef();

    // Update config when webshopCredentialsId prop changes
    useEffect(() => {
        setConfig(prevConfig => ({
            ...prevConfig,
            webshopCredentialsId: props.webshopCredentialsId
        }));
        // Clear data when switching webshop credentials to prevent old data from showing
        setData([]);
    }, [props.webshopCredentialsId]);

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions: gridOptions,
        });

        return () => {
            
        }
    }, []);

    const [returnId, setReturnId] = useState();
    const returnIdRef = useRef(returnId);
    
    const getReturnDetails = (params) => {
        if (!params) return;
    
        const newReturnId = parseInt(params.row.id);
        // Only update state if the returnId has actually changed
        if (returnIdRef.current !== newReturnId) {
            if (!spLoading) {
                setSpLoading(true);
            }
            setReturnId(newReturnId);
            returnIdRef.current = newReturnId; // Update the ref to the new value
        }
        setIsExtended(true);
    };

    const columns = [
        {field: "barcode", headerName: t("order"),flex:1,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return (
                    <Box
                        sx={{
                            width:1,
                            display:'flex',
                            flexDirection:"column",
                        }}
                    >
                        <Text>{params.row.order?.barcode ? "R" +params.row.order?.barcode : t("imported")}</Text>
                        <Text fontSize={13} light italic>{params.row.order?.external_reference}</Text>

                    </Box>
                );
            }
        },
        {field: "name", headerName: t("name"),flex:1,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return(
                    <Text>
                        {params.row.name}
                    </Text>
                )
            }
        },
        {field: "lineCount", headerName: t("sku_count"),flex:1,
            sortable:false,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return params.row.lineCount;
            }
        },        

        {field: "productCount", headerName: t("items"),flex:1,
            sortable:false,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return params.row.productCount;
            }
        },
        {
            field: "state", 
            headerName: t("status"),
            flex:1,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShLabelSkeleton/>
                }

                const { labelcolor, labeltext } = getReturnStateData(params.row.state, theme);
              
                return (
                    <ShLabel size={32} palette="normal" variant={labelcolor}>
                      {t(labeltext)}
                    </ShLabel>
                  );
              }
        },
        {
            field: "createdAt", 
            headerName: t("created_at"),
            flex:1,
            shFilter: {
                type: "date"
            },
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }

                let date = new Date(params.row.createdAt);
              
                return (
                    <Box>
                        <Text semibold>
                            {date.toLocaleDateString(i18n.language)}
                        </Text><br/>
                        <Text light>
                            {date.toLocaleTimeString(i18n.language,{timeStyle: 'short'})}
                        </Text>
                    </Box>
                );
            }
        },
        {
            field: "updatedAt", 
            headerName: t("last_updated"),
            flex:1,
            shFilter: {
                type: "date"
            },
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }

                if(params.row.updatedAt === params.row.createdAt || !params.row.order?.barcode){
                    return <Text light></Text>
                }

                let date = new Date(params.row.updatedAt);
              
                return (
                    <Box>
                        <Text semibold>
                            {date.toLocaleDateString(i18n.language)}
                        </Text><br/>
                        <Text light>
                            {date.toLocaleTimeString(i18n.language,{timeStyle: 'short'})}
                        </Text>
                    </Box>
                );
            }
        },
        {
            field: "phoneView", 
            headerName: "PhoneView",
            cellClassName: "phone-column--cell",
            renderCell: (params) => {
                return (
                    <RelationReturnCard onClick={() => getReturnDetails(params)} skeleton={params.row.skeleton ?? false} {...params.row}/>
                )
            }
        }
    ];

    return (
        <Box height={1} ref={ref} display={"flex"} flexDirection={"column"}>
            <ShDataGrid2 
                key={`${theme.palette.mode}-${config.webshopCredentialsId}`}
                content={useGetReturnsQuery}
                itemId={props.webshopCredentialsId}
                config={config}
                title={t('returns')}
                gridOptions={gridOptions} 
                columns={columns}
                rows={data}
                sortModel={{field: columns[0].field, type: "DESC"}}
                onRowClick={getReturnDetails}
            >
            </ShDataGrid2>

            {spLoading && 
                <SpReturnDetails
                    isExtended={isExtended}
                    setIsExtended={setIsExtended}
                    returnId={returnId}
                /> 
            }
        </Box>
    );
}

export default WebshopCredentialsReturns; 